import { useState } from 'react';
import { X, Zap, MapPin } from 'lucide-react';
import { businessRulesService, RuleType, RuleScope, RuleSeverity } from '../../services/business-rules.service';

interface CreateRuleModalProps {
  defaultSection: 'shift' | 'agent' | 'compliance';
  onClose: () => void;
  onSuccess: () => void;
}

// State-specific regulation presets
interface StatePreset {
  state: string;
  displayName: string;
  rules: {
    minRestHours: number;
    maxDailyHours: number;
    maxWeeklyHours: number;
    breakAfterHours: number;
    breakDuration: number;
    overtimeThreshold: number;
    doubleTimeThreshold?: number;
    mealBreakRequired: boolean;
    mealBreakDuration?: number;
  };
  description: string;
}

const statePresets: StatePreset[] = [
  {
    state: 'CA',
    displayName: 'California',
    rules: {
      minRestHours: 8,
      maxDailyHours: 8, // Overtime after 8 hours
      maxWeeklyHours: 40,
      breakAfterHours: 5, // Meal break required before 5th hour
      breakDuration: 30,
      overtimeThreshold: 8,
      doubleTimeThreshold: 12,
      mealBreakRequired: true,
      mealBreakDuration: 30,
    },
    description: 'CA: 30-min meal break before 5th hour, OT after 8hrs/day or 40hrs/week, 2x after 12hrs',
  },
  {
    state: 'NY',
    displayName: 'New York',
    rules: {
      minRestHours: 8,
      maxDailyHours: 10,
      maxWeeklyHours: 40,
      breakAfterHours: 6,
      breakDuration: 30,
      overtimeThreshold: 40,
      mealBreakRequired: true,
      mealBreakDuration: 30,
    },
    description: 'NY: 30-min meal break for 6+ hour shifts, OT after 40hrs/week, one day of rest per week',
  },
  {
    state: 'TX',
    displayName: 'Texas',
    rules: {
      minRestHours: 5,
      maxDailyHours: 12,
      maxWeeklyHours: 40,
      breakAfterHours: 8,
      breakDuration: 30,
      overtimeThreshold: 40,
      mealBreakRequired: false,
    },
    description: 'TX: No state-mandated meal breaks, OT after 40hrs/week (FLSA)',
  },
  {
    state: 'WA',
    displayName: 'Washington',
    rules: {
      minRestHours: 10,
      maxDailyHours: 12,
      maxWeeklyHours: 40,
      breakAfterHours: 5,
      breakDuration: 30,
      overtimeThreshold: 40,
      mealBreakRequired: true,
      mealBreakDuration: 30,
    },
    description: 'WA: 30-min meal break for 5+ hour shifts, 10-min rest per 4 hours, OT after 40hrs/week',
  },
  {
    state: 'FL',
    displayName: 'Florida',
    rules: {
      minRestHours: 8,
      maxDailyHours: 12,
      maxWeeklyHours: 40,
      breakAfterHours: 8,
      breakDuration: 30,
      overtimeThreshold: 40,
      mealBreakRequired: false,
    },
    description: 'FL: No state-mandated meal breaks for adults, OT after 40hrs/week (FLSA)',
  },
  {
    state: 'IL',
    displayName: 'Illinois',
    rules: {
      minRestHours: 8,
      maxDailyHours: 10,
      maxWeeklyHours: 40,
      breakAfterHours: 5,
      breakDuration: 20,
      overtimeThreshold: 40,
      mealBreakRequired: true,
      mealBreakDuration: 20,
    },
    description: 'IL: 20-min meal break for 7.5+ hour shifts, OT after 40hrs/week, 24 hours of rest per week',
  },
  {
    state: 'EU',
    displayName: 'European Union',
    rules: {
      minRestHours: 11,
      maxDailyHours: 8,
      maxWeeklyHours: 48,
      breakAfterHours: 6,
      breakDuration: 30,
      overtimeThreshold: 48,
      mealBreakRequired: true,
      mealBreakDuration: 30,
    },
    description: 'EU: 11 hours rest between shifts, max 48hrs/week averaged over 17 weeks, 30-min break for 6+ hours',
  },
  {
    state: 'FLSA',
    displayName: 'Federal (FLSA)',
    rules: {
      minRestHours: 8,
      maxDailyHours: 12,
      maxWeeklyHours: 40,
      breakAfterHours: 8,
      breakDuration: 30,
      overtimeThreshold: 40,
      mealBreakRequired: false,
    },
    description: 'FLSA: Federal standard - OT after 40hrs/week, no federal meal break requirements',
  },
];

// Quick presets for common scenarios
interface QuickPreset {
  id: string;
  name: string;
  section: 'shift' | 'agent' | 'compliance';
  icon: string;
  config: Partial<{
    ruleType: RuleType;
    name: string;
    description: string;
    severity: RuleSeverity;
    minShiftHours: number;
    maxShiftHours: number;
    breakAfterHours: number;
    breakDuration: number;
    maxWeeklyHours: number;
    maxConsecutiveDays: number;
    minRestHours: number;
  }>;
}

const quickPresets: QuickPreset[] = [
  {
    id: 'standard-8hr',
    name: 'Standard 8-Hour Shifts',
    section: 'shift',
    icon: '⏰',
    config: {
      ruleType: 'CHANNEL_HOURS',
      name: 'Standard 8-Hour Shift',
      description: 'Enforce standard 8-hour shifts with 30-minute break',
      severity: 'WARNING',
      minShiftHours: 7,
      maxShiftHours: 9,
      breakAfterHours: 4,
      breakDuration: 30,
    },
  },
  {
    id: 'no-overtime',
    name: 'Prevent Overtime (40hrs/week)',
    section: 'agent',
    icon: '⚠️',
    config: {
      ruleType: 'AGENT_CONSTRAINT',
      name: 'No Overtime - 40 Hour Maximum',
      description: 'Prevent scheduling over 40 hours per week per agent',
      severity: 'ERROR',
      maxWeeklyHours: 40,
      maxConsecutiveDays: 5,
    },
  },
  {
    id: 'part-time',
    name: 'Part-Time Limits (20hrs/week)',
    section: 'agent',
    icon: '👥',
    config: {
      ruleType: 'AGENT_CONSTRAINT',
      name: 'Part-Time Maximum Hours',
      description: 'Limit part-time agents to 20 hours per week',
      severity: 'ERROR',
      maxWeeklyHours: 20,
      maxConsecutiveDays: 4,
    },
  },
  {
    id: 'eu-compliance',
    name: 'EU Working Time Directive',
    section: 'compliance',
    icon: '🇪🇺',
    config: {
      ruleType: 'LABOR_LAW',
      name: 'EU Working Time Directive',
      description: '11 hours rest, max 48hrs/week, mandatory breaks',
      severity: 'CRITICAL',
      minRestHours: 11,
    },
  },
  {
    id: 'weekend-rotation',
    name: 'Fair Weekend Rotation',
    section: 'compliance',
    icon: '🔄',
    config: {
      ruleType: 'LABOR_LAW',
      name: 'Weekend Rotation Policy',
      description: 'Ensure fair distribution of weekend shifts',
      severity: 'WARNING',
    },
  },
];

export default function CreateRuleModal({ defaultSection, onClose, onSuccess }: CreateRuleModalProps) {
  const [showPresets, setShowPresets] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ruleType, setRuleType] = useState<RuleType>(getDefaultRuleType(defaultSection));
  const [scope, setScope] = useState<RuleScope>('ORGANIZATION');
  const [severity, setSeverity] = useState<RuleSeverity>('WARNING');
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Shift Rules specific fields
  const [minShiftHours, setMinShiftHours] = useState(4);
  const [maxShiftHours, setMaxShiftHours] = useState(12);
  const [breakAfterHours, setBreakAfterHours] = useState(4);
  const [breakDuration, setBreakDuration] = useState(30);
  const [allowSplitShifts, setAllowSplitShifts] = useState(false);

  // Agent Rules specific fields
  const [maxWeeklyHours, setMaxWeeklyHours] = useState(40);
  const [maxConsecutiveDays, setMaxConsecutiveDays] = useState(5);
  const [preferredDailyHours, setPreferredDailyHours] = useState(8);
  const [maxDailyHours, setMaxDailyHours] = useState(10);

  // Compliance specific fields
  const [minRestHours, setMinRestHours] = useState(11);
  const [weekendRotationWeeks, setWeekendRotationWeeks] = useState(2);
  const [maxWeekendDays, setMaxWeekendDays] = useState(1);

  const applyStatePreset = (preset: StatePreset) => {
    setName(`${preset.displayName} Labor Law Compliance`);
    setDescription(preset.description);
    setRuleType('LABOR_LAW');
    setSeverity('CRITICAL');
    setMinRestHours(preset.rules.minRestHours);
    setMaxDailyHours(preset.rules.maxDailyHours);
    setMaxWeeklyHours(preset.rules.maxWeeklyHours);
    setBreakAfterHours(preset.rules.breakAfterHours);
    setBreakDuration(preset.rules.breakDuration);
    setShowPresets(false);
    setSelectedPreset(preset.state);
  };

  const applyQuickPreset = (preset: QuickPreset) => {
    if (preset.config.name) setName(preset.config.name);
    if (preset.config.description) setDescription(preset.config.description);
    if (preset.config.ruleType) setRuleType(preset.config.ruleType);
    if (preset.config.severity) setSeverity(preset.config.severity);
    if (preset.config.minShiftHours !== undefined) setMinShiftHours(preset.config.minShiftHours);
    if (preset.config.maxShiftHours !== undefined) setMaxShiftHours(preset.config.maxShiftHours);
    if (preset.config.breakAfterHours !== undefined) setBreakAfterHours(preset.config.breakAfterHours);
    if (preset.config.breakDuration !== undefined) setBreakDuration(preset.config.breakDuration);
    if (preset.config.maxWeeklyHours !== undefined) setMaxWeeklyHours(preset.config.maxWeeklyHours);
    if (preset.config.maxConsecutiveDays !== undefined) setMaxConsecutiveDays(preset.config.maxConsecutiveDays);
    if (preset.config.minRestHours !== undefined) setMinRestHours(preset.config.minRestHours);
    setShowPresets(false);
    setSelectedPreset(preset.id);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a rule name');
      return;
    }

    setIsSaving(true);
    try {
      const conditions = buildConditions();
      const actions = buildActions();

      await businessRulesService.create({
        name,
        description: description || undefined,
        ruleType,
        scope,
        severity,
        isActive,
        conditions,
        actions,
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to create rule:', error);
      alert('Failed to create rule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const buildConditions = () => {
    switch (ruleType) {
      case 'CHANNEL_HOURS':
      case 'CAPACITY_TARGET':
        return {
          minShiftHours,
          maxShiftHours,
          breakAfterHours,
          breakDuration,
          allowSplitShifts,
        };
      case 'AGENT_CONSTRAINT':
      case 'TEAM_CONSTRAINT':
        return {
          maxWeeklyHours,
          maxConsecutiveDays,
          preferredDailyHours,
          maxDailyHours,
        };
      case 'LABOR_LAW':
        return {
          minRestHours,
          weekendRotationWeeks,
          maxWeekendDays,
        };
      default:
        return {};
    }
  };

  const buildActions = () => {
    return {
      enforce: severity === 'ERROR' || severity === 'CRITICAL',
      notify: true,
      message: `${name} violation detected`,
    };
  };

  const filteredQuickPresets = quickPresets.filter(p => p.section === defaultSection);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Business Rule</h2>
            <p className="text-sm text-gray-600 mt-1">
              {showPresets ? 'Choose a preset or start from scratch' : 'Configure your business rule'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Preset Selection View */}
        {showPresets && (
          <div className="p-6 space-y-6">
            {/* Quick Presets */}
            {filteredQuickPresets.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Zap className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Quick Presets</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {filteredQuickPresets.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => applyQuickPreset(preset)}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{preset.icon}</span>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 group-hover:text-purple-700">
                            {preset.name}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {preset.config.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* State/Region Presets - Only for compliance section */}
            {defaultSection === 'compliance' && (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <MapPin className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">State & Regional Regulations</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {statePresets.map((preset) => (
                    <button
                      key={preset.state}
                      onClick={() => applyStatePreset(preset)}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 group-hover:text-green-700">
                          {preset.displayName}
                        </h4>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {preset.state}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {preset.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Start from Scratch */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowPresets(false)}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-center group"
              >
                <div className="text-gray-600 group-hover:text-purple-700 font-medium">
                  Or start from scratch →
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Form View */}
        {!showPresets && (
          <>
            <div className="p-6 space-y-6">
              {/* Show selected preset indicator */}
              {selectedPreset && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Zap className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-900">
                      Based on preset: <strong>{selectedPreset}</strong>
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setShowPresets(true);
                      setSelectedPreset(null);
                    }}
                    className="text-xs text-blue-700 hover:text-blue-900 font-medium"
                  >
                    Change Preset
                  </button>
                </div>
              )}

              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Maximum 8-hour shifts"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Optional description..."
                />
              </div>

              {/* Rule Configuration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rule Type
                  </label>
                  <select
                    value={ruleType}
                    onChange={(e) => setRuleType(e.target.value as RuleType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <optgroup label="Shift Rules">
                      <option value="CHANNEL_HOURS">Channel Hours</option>
                      <option value="CAPACITY_TARGET">Capacity Target</option>
                    </optgroup>
                    <optgroup label="Agent Rules">
                      <option value="AGENT_CONSTRAINT">Agent Constraint</option>
                      <option value="TEAM_CONSTRAINT">Team Constraint</option>
                    </optgroup>
                    <optgroup label="Compliance">
                      <option value="LABOR_LAW">Labor Law</option>
                      <option value="HOLIDAY">Holiday</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope
                  </label>
                  <select
                    value={scope}
                    onChange={(e) => setScope(e.target.value as RuleScope)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="ORGANIZATION">Organization-wide</option>
                    <option value="TEAM">Team-specific</option>
                    <option value="AGENT">Agent-specific</option>
                    <option value="CHANNEL">Channel-specific</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity
                  </label>
                  <select
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value as RuleSeverity)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="INFO">Info - Informational only</option>
                    <option value="WARNING">Warning - Show warning</option>
                    <option value="ERROR">Error - Prevent action</option>
                    <option value="CRITICAL">Critical - Hard constraint</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center space-x-2 pt-7">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Rule is active</span>
                  </label>
                </div>
              </div>

              {/* Type-specific fields */}
              {(ruleType === 'CHANNEL_HOURS' || ruleType === 'CAPACITY_TARGET') && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Shift Constraints</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min Shift Length (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={minShiftHours}
                        onChange={(e) => setMinShiftHours(parseFloat(e.target.value) || 4)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Shift Length (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={maxShiftHours}
                        onChange={(e) => setMaxShiftHours(parseFloat(e.target.value) || 12)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Break Required After (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        step="0.5"
                        value={breakAfterHours}
                        onChange={(e) => setBreakAfterHours(parseFloat(e.target.value) || 4)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Break Duration (minutes)
                      </label>
                      <input
                        type="number"
                        min="15"
                        max="120"
                        step="15"
                        value={breakDuration}
                        onChange={(e) => setBreakDuration(parseInt(e.target.value) || 30)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={allowSplitShifts}
                          onChange={(e) => setAllowSplitShifts(e.target.checked)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Allow split shifts</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {(ruleType === 'AGENT_CONSTRAINT' || ruleType === 'TEAM_CONSTRAINT') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Agent Constraints</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Weekly Hours
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        step="0.5"
                        value={maxWeeklyHours}
                        onChange={(e) => setMaxWeeklyHours(parseFloat(e.target.value) || 40)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Consecutive Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="14"
                        value={maxConsecutiveDays}
                        onChange={(e) => setMaxConsecutiveDays(parseInt(e.target.value) || 5)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Daily Hours
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={preferredDailyHours}
                        onChange={(e) => setPreferredDailyHours(parseFloat(e.target.value) || 8)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Daily Hours
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={maxDailyHours}
                        onChange={(e) => setMaxDailyHours(parseFloat(e.target.value) || 10)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {ruleType === 'LABOR_LAW' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Compliance Requirements</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min Rest Between Shifts (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="0.5"
                        value={minRestHours}
                        onChange={(e) => setMinRestHours(parseFloat(e.target.value) || 11)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">EU requires 11 hours, CA requires 8 hours</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Weekend Rotation (weeks)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={weekendRotationWeeks}
                        onChange={(e) => setWeekendRotationWeeks(parseInt(e.target.value) || 2)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Weekend Days per Period
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="8"
                        value={maxWeekendDays}
                        onChange={(e) => setMaxWeekendDays(parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowPresets(true)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to Presets
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSaving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>{isSaving ? 'Creating...' : 'Create Rule'}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function getDefaultRuleType(section: string): RuleType {
  switch (section) {
    case 'shift':
      return 'CHANNEL_HOURS';
    case 'agent':
      return 'AGENT_CONSTRAINT';
    case 'compliance':
      return 'LABOR_LAW';
    default:
      return 'CHANNEL_HOURS';
  }
}
